import React, { Component } from 'react';
import { ContentGroup } from 'ui';
import { hasFeatureFlags } from 'utils/features';
import { TabList, Tab } from 'components/shared/TabbedView';
import Select from 'components/shared/select';
import NoActivity from 'components/Learn/svg/NoActivity.jsx';
import NoCompleted from 'components/Learn/svg/NoCompleted.jsx';
import ContentCard from '../../shared/LearnContentCard';

/*::
type AssignmentGroupProps = {
  title: string,
  assignments: Array<Object>,
  sortable: boolean,
  defaultSortOption: SortOption,
  sortOptions: {
    [SortOptionValue]: SortOption
  },
}

type SortOptionValue = string;

type SortOption = {
  label: string,
  sort: (a: any, b: any) => number,
}
*/

class AssignmentGroup extends Component {
  constructor(props /*: AssignmentGroupProps */) {
    super(props);
    this.state = {
      selectedSortOption: props.defaultSortOption,
      selectedIndex: 0,
      hasLearningJourneys: hasFeatureFlags(['readiness-learning-journeys']),
    };
    this.onSortChange = this.onSortChange.bind(this);
  }

  onSortChange(e) {
    this.setState({ selectedSortOption: e.target.value });
  }

  getAssignments() {
    const { assignments, sortable, sortOptions } = this.props;
    if (sortable) {
      const { selectedSortOption } = this.state;
      const { sort: compareFn } = sortOptions[selectedSortOption];
      return assignments.sort(compareFn);
    }
    return assignments;
  }

  getJourneyAssignments() {
    const { journeyAssignments, sortable, journeySortOptions } = this.props;
    if (sortable) {
      const { selectedSortOption } = this.state;
      const { sort: compareFn } = journeySortOptions[selectedSortOption];
      return journeyAssignments.sort(compareFn);
    }
    return journeyAssignments;
  }

  render() {
    const { selectedSortOption } = this.state;
    const { sortOptions } = this.props;
    const items =
      this.state.selectedIndex === 0
        ? this.getAssignments()
        : this.getJourneyAssignments();

    const journeysTitle = this.props.completed
      ? t('assignments.no_assignments.completed.title.journeys')
      : t('assignments.no_assignments.incomplete.title.journeys');
    const trainingContentTitle = this.props.completed
      ? t('assignments.no_assignments.completed.title.training_content')
      : t('assignments.no_assignments.incomplete.title.training_content');

    const journeysDescription = this.props.completed
      ? t('assignments.no_assignments.completed.description.journeys')
      : t('assignments.no_assignments.incomplete.description.journeys');
    const trainingContentDescription = this.props.completed
      ? t('assignments.no_assignments.completed.description.training_content')
      : t('assignments.no_assignments.incomplete.description.training_content');

    const emptyTitle =
      this.state.selectedIndex === 1 ? journeysTitle : trainingContentTitle;
    const emptyDescription =
      this.state.selectedIndex === 1
        ? journeysDescription
        : trainingContentDescription;
    const hasOverdueAssignments =
      this.state.hasLearningJourneys &&
      this.props.assignments &&
      this.props.assignments.findIndex(
        assignment => assignment.progress.isOverdue,
      ) !== -1;

    const hasOverdueJourneyAssignments =
      this.state.hasLearningJourneys &&
      this.props.journeyAssignments &&
      this.props.journeyAssignments.findIndex(
        assignment => assignment.progress.isOverdue,
      ) !== -1;

    return (
      <div className="mb-16">
        <ContentGroup
          className="LearnContentGroup"
          items={items}
          title={this.props.title}
        >
          {this.state.hasLearningJourneys && this.props.journeyAssignments && (
            <ContentGroup.TabbedView
              onSelect={index => this.setState({ selectedIndex: index })}
              selectedIndex={this.state.selectedIndex}
            >
              <TabList>
                <Tab>
                  <div>
                    {t('contents.index.toggle.training_content')}
                    <div className="TabbedView-count">
                      {this.props.assignments.length}
                      {hasOverdueAssignments && (
                        <div className="TabbedView-overdue" />
                      )}
                    </div>
                  </div>
                </Tab>
                <Tab>
                  <div>
                    {t('contents.index.toggle.learning_journeys')}
                    <div className="TabbedView-count">
                      {this.props.journeyAssignments.length}
                      {hasOverdueJourneyAssignments && (
                        <div className="TabbedView-overdue" />
                      )}
                    </div>
                  </div>
                </Tab>
              </TabList>
            </ContentGroup.TabbedView>
          )}
          {this.props.sortable && (
            <ContentGroup.Action>
              <div>
                <span className="mr-8">{t('content.sort.prompt')}</span>
                <Select
                  onChange={this.onSortChange}
                  value={selectedSortOption}
                  ariaLabel={t('content.sort.aria_label_sorting_options')}
                >
                  {Object.entries(sortOptions).map(([value, option]) => (
                    <option key={value} value={value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            </ContentGroup.Action>
          )}
          {items.length === 0 ? (
            <ContentGroup.EmptyScreen>
              <div className="ContentGroupEmptyScreen-content">
                {this.props.completed ? <NoCompleted /> : <NoActivity />}
                <div className="ContentGroupEmptyScreen-textMain">
                  {emptyTitle}
                </div>
                <div className="ContentGroupEmptyScreen-textBody">
                  {emptyDescription}
                </div>
              </div>
            </ContentGroup.EmptyScreen>
          ) : (
            <ContentGroup.Items
              render={assignment => (
                <ContentCard
                  key={`${assignment.id}.${assignment.type}`}
                  {...assignment}
                />
              )}
            />
          )}
        </ContentGroup>
      </div>
    );
  }
}

export default AssignmentGroup;
